import React, { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n/hooks";
import { Button } from "@/components/Base/Button";
import { useUserDataFlattened } from "@/modules";
import {
  useEnableDownloadAppFloatingOnHomeTop,
  useLoginRegisterConfig,
} from "@/modules/homeDisplay/hooks";
import { useCurrencySymbol } from "@/hooks/useUtils";
import { useNavigation, ROUTES } from "@/utils/navigation";
import { openLoginRegisterPopup } from "@/components/LoginRegisterPopup";
import { openDepositPopup } from "@/components/DepositPopup";
import { PromoBubble } from "@/components/PromoBubble";
import { DownloadAppBar } from "@/components/DownloadAppBar";
import "./Header.scss";
import type { HeaderProps } from "../../Header.types";
import InsuranceIconImg from "@/assets/homePage/reskin/insurance.webp";
import DefaultAvatarImg from "@/assets/profile/VIP0.webp";
import { Icon } from "@/components/Icon";
import { utils } from "@/index";
import { isDownloadAppBarSessionClosed } from "@/utils/downloadAppBarSession";

export const Header = ({ className = "", pageType = "home" }: HeaderProps) => {
  const { t } = useI18n();
  const { navigate } = useNavigation();
  const { isAuthenticated, data } = useUserDataFlattened();
  const enableFloatingOnHomeTop = useEnableDownloadAppFloatingOnHomeTop();
  const balance = data?.balanceDetail?.balance ?? data?.balance;
  const currencySymbol = useCurrencySymbol();
  const [balanceVisible, setBalanceVisible] = useState(true);
  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const el = headerRef.current;
    if (!el) return;
    const sync = () => {
      const h = el.getBoundingClientRect().height;
      document.documentElement.style.setProperty("--header-height", `${h}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
    // Layout 是持久壳，非 home 早退渲染 null 后 el 为空；依赖 pageType 保证
    // 切回 home 重挂 DOM 时能重新建立 observer（az 源是 [] —— 它从不渲染 null）
  }, [pageType]);

  const loginRegisterConfig = useLoginRegisterConfig();
  const homeRegisterPromoText =
    loginRegisterConfig?.homeRegisterBtnFirstChargeText?.trim() ?? "";

  const [downloadAppBarVisible, setDownloadAppBarVisible] = useState(() => {
    if (utils.isApp() || utils.updateCheckIsPwa() || !enableFloatingOnHomeTop)
      return false;
    return !isDownloadAppBarSessionClosed();
  });
  useEffect(() => {
    const handler = (e: Event) =>
      setDownloadAppBarVisible((e as CustomEvent).detail);
    window.addEventListener("downloadAppBarChange", handler);
    return () => window.removeEventListener("downloadAppBarChange", handler);
  }, []);

  const handleLoginClick = () => {
    openLoginRegisterPopup({ defaultTab: "sms" });
  };

  const handleAvatarClick = () => {
    navigate(ROUTES.PROFILE, { state: { fromBottomNav: true } });
  };

  const handleBalanceClick = () => {
    navigate(ROUTES.ACCOUNT_DETAILS);
  };

  const handleDepositClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openDepositPopup();
  };

  // az 侧 Layout 只在 HOME 渲染顶栏（VIP/Profile 由页面自带头部），本分支共享
  // Layout 对这三个路由都挂 Header——classic 对非 home pageType 渲染空以对齐 az。
  // 注意早退必须在全部 hooks 之后（React hooks 规则）。
  if (pageType !== "home") {
    return null;
  }

  return (
    <>
      {/* az 侧由 Layout 挂载 DownloadAppBar；Layout 是跨 preset 共享层，
          这里收进 classic variant 内挂载（组件自带 config/路由/关闭三重显隐门），
          保证 card preset 的 bundle 与行为不受影响 */}
      <DownloadAppBar />
      {/* DownloadAppBar 是 position:fixed（脱离文档流），原先靠 header 的 margin-top 让位。
          该写法在 PC 端会失效并叠成"两个头部"：外层 .mobile-layout__header 被 transform
          居中后成为 fixed 的包含块，header 的 margin-top 又穿透无 padding 的容器发生折叠，
          导致下载条与用户栏落在同一偏移上、上方留空。改用与下载条等高的 in-flow 占位块
          预留空间（高度取 DownloadAppBar 自身的 --height-48），PC / 移动端行为一致，
          也不再写死 3rem。 */}
      {downloadAppBarVisible && (
        <div className="mobile-header__download-slot" aria-hidden="true" />
      )}
      <header
        ref={headerRef}
        className={`mobile-header ${isAuthenticated ? "mobile-header--authenticated" : ""} ${className}`}
      >
        <div className="mobile-header__content">
          {isAuthenticated ? (
            /* 已登录：复用 auth-bar 容器改造成用户信息（头像/余额/客服/搜索） */
            <div className="mobile-header__auth-bar">
              {/* 用户头像 */}
              <div
                className="mobile-header__avatar"
                onClick={handleAvatarClick}
                role="button"
                tabIndex={0}
              >
                <img
                  src={
                    data?.avatar
                      ? utils.getImageUrl(data.avatar)
                      : DefaultAvatarImg
                  }
                  alt="avatar"
                  className="mobile-header__avatar-img"
                />
              </div>

              {/* 余额区块（币种符号 + 金额 + eye + 充值） */}
              <div className="mobile-header__balance-wrapper">
                <div
                  className="mobile-header__balance"
                  onClick={handleBalanceClick}
                >
                  <span className="balance-currency">{currencySymbol}</span>
                  <span className="balance-amount">
                    {balanceVisible
                      ? typeof balance === "number" && balance != null
                        ? balance.toString().split(".")[1]?.length > 2
                          ? balance.toFixed(2)
                          : balance
                        : "0"
                      : "***"}
                  </span>
                </div>
                <img
                  src={InsuranceIconImg}
                  alt=""
                  className="mobile-header__insurance-icon"
                  draggable={false}
                />
                <button
                  className="mobile-header__eye-btn"
                  onClick={() => setBalanceVisible(v => !v)}
                  aria-label={balanceVisible ? "Hide balance" : "Show balance"}
                >
                  <Icon
                    name={balanceVisible ? "eye-closed" : "eye"}
                    size={16}
                  />
                </button>
              </div>

              {/* 充值按钮 */}
              <Button
                variant="primary"
                size="sm"
                className="mobile-header__deposit-btn"
                onClick={handleDepositClick}
              >
                {t("common.deposit")}
              </Button>

              {/* 弹性空白，将客服/搜索推到右侧 */}
              <div className="mobile-header__spacer" />

              {/* 客服（棕色图标，背景是白色圆形） */}
              <button
                className="mobile-header__service-icon"
                onClick={() => navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=1`)}
                aria-label="Customer Service"
              >
                <span
                  className="mobile-header__auth-bar-icon-img"
                  aria-hidden="true"
                />
              </button>

              {/* 搜索（棕色图标） */}
              <button
                className="mobile-header__search-icon"
                onClick={() => navigate("/game-search")}
                aria-label="Search"
              >
                <span
                  className="mobile-header__auth-bar-icon-img"
                  aria-hidden="true"
                />
              </button>
            </div>
          ) : (
            /* 未登录：全宽 auth-bar，从左到右：Login | 注册 CTA | 客服 | 搜索 */
            <div className="mobile-header__auth-bar">
              <button
                className="mobile-header__login-btn"
                onClick={handleLoginClick}
              >
                {t("auth.login")}
              </button>
              <div className="mobile-header__register-btn-wrap">
                <PromoBubble text={homeRegisterPromoText} />
                <Button
                  variant="primary"
                  size="sm"
                  className="mobile-header__register-btn"
                  onClick={() => openLoginRegisterPopup({ defaultTab: "sms" })}
                >
                  {t("auth.registerToWinBig")}
                </Button>
              </div>
              <button
                className="mobile-header__service-icon"
                onClick={() => navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=1`)}
                aria-label="Customer Service"
              >
                <span
                  className="mobile-header__auth-bar-icon-img"
                  aria-hidden="true"
                />
              </button>
              <button
                className="mobile-header__search-icon"
                onClick={() => navigate("/game-search")}
                aria-label="Search"
              >
                <span
                  className="mobile-header__auth-bar-icon-img"
                  aria-hidden="true"
                />
              </button>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
